import { Divider, Stack } from '@mui/material';
import { useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { useRecoilCallback, useRecoilValue, useSetRecoilState } from 'recoil';
import { RoiId } from '~/domain/changes/ids';
import {
    domainEventBus,
    RoiVisibilityChangeRequestedEvent,
} from '~/domain/events';
import {
    RoiReadModel,
    StructureSetReadModel,
} from '~/domain/readmodels/StructureSetReadModel';
import useViewportFocus from '~/hooks/useViewportFocus';
import {
    autoShownRoiNumberState,
    selectedROIState,
} from '~/state/atoms/structureSet/roiAtoms';
import StructureSet from '../../ContouringStep/StructureSet/StructureSet';
import ReviewControls from './ReviewControls';
import ReviewPlayer from './ReviewPlayer';

/**
 * Each structure's own visibility, by ROI number: the structure `selection`
 * shows only because it is selected counts as hidden.
 */
function ownVisibilityOf(
    rois: readonly RoiReadModel[],
    selection: { roiNumber: number; autoShownRoiNumber: number | null }
): Map<number, boolean> {
    return new Map(
        rois.map((r) => [
            r.roiNumber,
            r.visible &&
                !(
                    r.roiNumber === selection.roiNumber &&
                    r.roiNumber === selection.autoShownRoiNumber
                ),
        ])
    );
}

export default function ReviewStructureSet({
    structureSet,
}: {
    structureSet: StructureSetReadModel;
}) {
    const selectedROINumber = useRecoilValue(selectedROIState);
    const { focusOnStructure } = useViewportFocus();
    const readSelection = useRecoilCallback(
        ({ snapshot }) =>
            () => ({
                roiNumber: snapshot.getLoadable(selectedROIState).getValue(),
                autoShownRoiNumber: snapshot
                    .getLoadable(autoShownRoiNumberState)
                    .getValue(),
            }),
        []
    );
    const setAutoShown = useSetRecoilState(autoShownRoiNumberState);
    const shownSetUidRef = useRef(structureSet.dicomUid);
    shownSetUidRef.current = structureSet.dicomUid;
    const ownVisibilityBySetRef = useRef(
        new Map<string, Map<number, boolean>>()
    );

    // Review owns visibility while mounted. The first time it shows a
    // structure set it records each structure's own visibility, and reuses
    // that record when it shows the set again, by when the set carries
    // Review's hiding. On unmount it restores the record of the set shown
    // and re-applies the selection rule to the structure selected now:
    // shown, and marked auto-shown only if its own visibility is hidden, so
    // a later deselect cannot hide a structure the user had shown.
    // Visibility requests and the selection belong to the current set, so a
    // record is applied only while its own set is the one shown.
    useEffect(() => {
        const recorded = ownVisibilityBySetRef.current;
        const ownVisibility =
            recorded.get(structureSet.dicomUid) ??
            ownVisibilityOf(structureSet.roiArray, readSelection());
        recorded.set(structureSet.dicomUid, ownVisibility);
        return () => {
            if (shownSetUidRef.current !== structureSet.dicomUid) {
                return;
            }
            const selected = readSelection().roiNumber;
            ownVisibility.forEach((visible, roiNumber) => {
                domainEventBus.publish(
                    new RoiVisibilityChangeRequestedEvent(
                        roiNumber,
                        visible || roiNumber === selected,
                        'user'
                    )
                );
            });
            setAutoShown(
                ownVisibility.get(selected) === false ? selected : null
            );
        };
        // structureSet.roiArray intentionally omitted. The snapshot is
        // taken once per structure set, not on every visibility update.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [structureSet.dicomUid]);

    // When the selected ROI changes, hide all other ROIs, make the selected
    // one visible, and auto-focus the viewport.
    useEffect(() => {
        if (selectedROINumber === -1) {
            return;
        }
        structureSet.roiArray.forEach((roi) => {
            domainEventBus.publish(
                new RoiVisibilityChangeRequestedEvent(
                    roi.roiNumber,
                    roi.roiNumber === selectedROINumber,
                    'user'
                )
            );
        });
        focusOnStructure(selectedROINumber);
        // focusOnStructure intentionally omitted
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedROINumber, structureSet.dicomUid]);

    const renderReviewExtras = (
        roi: RoiReadModel,
        roiId: RoiId | undefined
    ) => (
        <Stack spacing={1.5} mt={2}>
            <Divider />
            <ReviewPlayer roi={roi} showLabel={false} />
            <ReviewControls roi={roi} roiId={roiId} />
        </Stack>
    );

    return (
        // The Review step reviews and approves what contouring produced; it is
        // not an authoring surface. readOnly keeps ctrl-click multi-select (and
        // its Copy) available while withholding structure creation and the bulk
        // Delete, which was otherwise reachable here on an unapproved set.
        <StructureSet
            structureSet={structureSet}
            renderExtraDetails={renderReviewExtras}
            readOnly
            selectHint={
                <FormattedMessage id="workflow.review.selectStructureToStart" />
            }
        />
    );
}
